import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Gauge,
  IndianRupee,
  Lightbulb,
  LineChart,
  RefreshCw,
  ShieldAlert,
  Target,
  TrendingUp,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { EmptyState } from "../components/CommandSystem";
import { HoardexaLoader } from "../components/HoardexaLoader";
import api from "../lib/api";
import { fmtDate, fmtNumber, fmtPercent, inr } from "../lib/format";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function clampPct(value) {
  return Math.max(0, Math.min(100, Number(value || 0)));
}

const TABS = [
  { key: "command", label: "Command Center", icon: Lightbulb },
  { key: "scorecards", label: "Scorecards", icon: Gauge },
  { key: "demand", label: "Demand Calendar", icon: CalendarDays },
  { key: "pricing", label: "Pricing", icon: IndianRupee },
  { key: "funnel", label: "Proposal Funnel", icon: LineChart },
  { key: "portfolio", label: "Portfolio", icon: Target },
  { key: "risks", label: "Risks", icon: ShieldAlert },
  { key: "confidence", label: "Data Confidence", icon: ClipboardCheck },
];

const TONE = {
  critical: "rose",
  warning: "amber",
  success: "emerald",
  info: "blue",
  high: "emerald",
  medium: "amber",
  low: "rose",
  missing: "rose",
};

function Badge({ children, tone = "slate" }) {
  return <span className={`hx-badge tone-${tone}`}>{children}</span>;
}

function Metric({ label, value, subtext, icon: Icon, tone = "blue" }) {
  return (
    <div className={`hx-analysis-metric tone-${tone}`}>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {subtext ? <small>{subtext}</small> : null}
      </div>
      <div className="hx-analysis-metric-icon">
        <Icon size={17} />
      </div>
    </div>
  );
}

function ConfidenceBar({ value }) {
  const pct = clampPct(value);
  return (
    <div className="hx-intel-confidence">
      <span>Confidence</span>
      <div><i style={{ width: `${pct}%` }} /></div>
      <b>{fmtPercent(pct)}</b>
    </div>
  );
}

function InsightCard({ item, index }) {
  return (
    <article className={`hx-intel-card severity-${item.severity || "info"}`}>
      <header>
        <div className="hx-intel-rank">{index + 1}</div>
        <div>
          <h3>{item.action}</h3>
          <p>{item.observation}</p>
        </div>
        <Badge tone={TONE[item.severity] || "slate"}>{item.severity || "info"}</Badge>
      </header>
      <div className="hx-intel-narrative">
        <div>
          <small>Evidence</small>
          <p>{item.evidence}</p>
        </div>
        <div>
          <small>Explanation</small>
          <p>{item.explanation}</p>
        </div>
        <div>
          <small>Recommendation</small>
          <p>{item.recommendation}</p>
        </div>
      </div>
      <footer>
        <div>
          <small>Financial impact</small>
          <strong>{inr(item.financial_impact_low)} to {inr(item.financial_impact_high)}</strong>
        </div>
        <ConfidenceBar value={item.confidence} />
      </footer>
      {item.caveat ? <p className="hx-intel-caveat">{item.caveat}</p> : null}
    </article>
  );
}

function CommandCenter({ data }) {
  const cards = asArray(data.command_center);
  return (
    <div className="hx-intel-grid">
      {cards.length === 0 ? (
        <EmptyState title="No decision cards yet" description="Refresh Analysis after adding campaigns, proposals, hoardings or financial records." />
      ) : cards.map((item, index) => <InsightCard key={item.id || index} item={item} index={index} />)}
    </div>
  );
}

function Scorecards({ data }) {
  const rows = asArray(data.scorecards);
  return (
    <section className="hx-analysis-under-section">
      <div className="hx-analysis-section-head">
        <div>
          <span>Hoarding commercial scorecards</span>
          <h2>Every asset, scored for demand, pricing, contribution and risk</h2>
        </div>
        <Badge tone="blue">{rows.length} hoardings</Badge>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No scorecards generated" description="Run a refresh once campaign and hoarding data is available." />
      ) : (
        <div className="hx-intel-table-wrap">
          <table className="hx-intel-table">
            <thead>
              <tr>
                <th>Hoarding</th>
                <th>Action</th>
                <th>Commercial</th>
                <th>Occupancy</th>
                <th>Proposal demand</th>
                <th>Contribution</th>
                <th>Category</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.hoarding_id}>
                  <td>
                    <strong>{row.name}</strong>
                    <small>{row.hoarding_code} · {row.area}, {row.city}</small>
                  </td>
                  <td><Badge tone={row.recommended_action === "Watch" ? "slate" : "amber"}>{row.recommended_action}</Badge></td>
                  <td><Score value={row.commercial_score} /></td>
                  <td>{fmtPercent(row.occupancy_score)}</td>
                  <td>{fmtNumber(row.proposal_appearances, 0)} appearances</td>
                  <td>{inr(row.net_contribution)}</td>
                  <td>{row.top_category}</td>
                  <td>{fmtPercent(row.confidence)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Score({ value }) {
  const pct = clampPct(value);
  return (
    <div className="hx-score-pill">
      <span>{fmtNumber(pct, 0)}</span>
      <div><i style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

function DemandCalendar({ data }) {
  const cells = asArray(data.demand_calendar);
  const top = cells.filter((c) => c.demand_label !== "Sparse").slice(0, 60);
  return (
    <section className="hx-analysis-under-section">
      <div className="hx-analysis-section-head">
        <div>
          <span>Demand forecast</span>
          <h2>Expected booked days and booking probability for upcoming months</h2>
        </div>
        <Badge tone="blue">{top.length} signals</Badge>
      </div>
      {top.length === 0 ? (
        <EmptyState title="Demand calendar needs more history" description="Seasonal signals appear once multiple comparable periods exist." />
      ) : (
        <div className="hx-demand-grid">
          {top.map((cell, index) => (
            <article key={`${cell.entity_id}-${cell.period_month}-${index}`} className={`hx-demand-cell label-${String(cell.demand_label || "").toLowerCase()}`}>
              <header>
                <strong>{cell.period_month}</strong>
                <Badge tone={cell.demand_label === "Strong" ? "emerald" : cell.demand_label === "Moderate" ? "blue" : "slate"}>{cell.demand_label}</Badge>
              </header>
              <p>{cell.area}, {cell.city}</p>
              <div>
                <span>{fmtNumber(cell.expected_booked_days)} days</span>
                <span>{fmtPercent(cell.booking_probability * 100)} probability</span>
              </div>
              <small>{cell.supporting_evidence}</small>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function Pricing({ data }) {
  const rows = asArray(data.pricing);
  return (
    <section className="hx-analysis-under-section">
      <div className="hx-analysis-section-head">
        <div>
          <span>Pricing intelligence</span>
          <h2>Fair price, future quote, floor price and price position</h2>
        </div>
        <Badge tone="amber">{rows.filter((r) => r.price_position !== "Fair").length} moves</Badge>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No pricing recommendations" description="Pricing requires listed rates and comparable demand or transaction records." />
      ) : (
        <div className="hx-pricing-grid">
          {rows.slice(0, 80).map((row) => (
            <article key={row.hoarding_id} className="hx-pricing-card">
              <header>
                <div>
                  <h3>{row.name}</h3>
                  <p>{row.hoarding_code} · {row.area}, {row.city}</p>
                </div>
                <Badge tone={row.price_position === "Underpriced" || row.price_position === "Peak demand" ? "emerald" : row.price_position === "Overpriced" || row.price_position === "Weak demand" ? "amber" : "slate"}>
                  {row.price_position}
                </Badge>
              </header>
              <div className="hx-price-stack">
                <div><small>Current</small><strong>{inr(row.current_price)}</strong></div>
                <div><small>Fair range</small><strong>{inr(row.fair_price_low)} to {inr(row.fair_price_high)}</strong></div>
                <div><small>Recommended</small><strong>{inr(row.recommended_price)}</strong></div>
                <div><small>Minimum acceptable</small><strong>{inr(row.minimum_acceptable_price)}</strong></div>
              </div>
              <p>{row.recommendation}</p>
              <ConfidenceBar value={row.confidence} />
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ProposalFunnel({ data }) {
  const rows = asArray(data.proposal_funnels);
  return (
    <section className="hx-analysis-under-section">
      <div className="hx-analysis-section-head">
        <div>
          <span>Proposal demand</span>
          <h2>Shown, selected, shared, opened, accepted and booked</h2>
        </div>
        <Badge tone="blue">{rows.length} funnels</Badge>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No proposal funnel yet" description="Proposal item facts build once saved proposals include hoardings." />
      ) : (
        <div className="hx-funnel-list">
          {rows.slice(0, 80).map((row) => <FunnelRow row={row} key={row.entity_id} />)}
        </div>
      )}
    </section>
  );
}

function FunnelRow({ row }) {
  const max = Math.max(row.shown, row.selected, row.shared, row.opened, row.accepted, row.booked, 1);
  const stages = [
    ["Shown", row.shown],
    ["Selected", row.selected],
    ["Shared", row.shared],
    ["Opened", row.opened],
    ["Accepted", row.accepted],
    ["Booked", row.booked],
  ];
  return (
    <article className="hx-funnel-row">
      <header>
        <div>
          <h3>{row.name || row.entity_id}</h3>
          <p>{row.diagnosis}</p>
        </div>
        <strong>{inr(row.converted_value)} / {inr(row.quoted_value)}</strong>
      </header>
      <div className="hx-funnel-bars">
        {stages.map(([label, count]) => (
          <div key={label}>
            <span>{label}</span>
            <div><i style={{ width: `${(Number(count || 0) / max) * 100}%` }} /></div>
            <b>{fmtNumber(count, 0)}</b>
          </div>
        ))}
      </div>
    </article>
  );
}

function Portfolio({ data }) {
  const cards = asArray(data.portfolio);
  return (
    <div className="hx-intel-grid">
      {cards.length === 0 ? (
        <EmptyState title="No portfolio decisions yet" description="Portfolio actions appear once scorecards detect price, demand, contribution or risk signals." />
      ) : cards.map((item, index) => <InsightCard key={item.id || index} item={item} index={index} />)}
    </div>
  );
}

function Risks({ data }) {
  const rows = asArray(data.operational_risks);
  return (
    <section className="hx-analysis-under-section">
      <div className="hx-analysis-section-head">
        <div>
          <span>Operational risk</span>
          <h2>Proof, maintenance, delivery and schedule issues that can distort analysis</h2>
        </div>
        <Badge tone="rose">{rows.length} risks</Badge>
      </div>
      {rows.length === 0 ? (
        <EmptyState title="No operational risks detected" description="Proof-photo and maintenance checks are currently clean." />
      ) : (
        <div className="hx-risk-grid">
          {rows.map((risk) => (
            <article key={risk.id} className={`hx-risk-card severity-${risk.severity}`}>
              <header>
                <AlertTriangle size={17} />
                <Badge tone={TONE[risk.severity] || "amber"}>{risk.severity}</Badge>
              </header>
              <h3>{risk.title}</h3>
              <p>{risk.evidence}</p>
              <strong>{risk.recommended_action}</strong>
              <footer>
                <span>Exposure {inr(risk.financial_exposure)}</span>
                <span>{fmtPercent(risk.confidence)} confidence</span>
              </footer>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function DataConfidence({ data }) {
  const rows = asArray(data.data_confidence);
  return (
    <section className="hx-analysis-under-section">
      <div className="hx-analysis-section-head">
        <div>
          <span>Model trust</span>
          <h2>What Analysis can trust, and what data still limits it</h2>
        </div>
        <Badge tone="blue">{rows.length} checks</Badge>
      </div>
      <div className="hx-confidence-grid">
        {rows.map((row) => (
          <article key={row.metric_key} className={`hx-confidence-card status-${row.status}`}>
            <header>
              <h3>{row.label}</h3>
              <Badge tone={TONE[row.status] || "slate"}>{row.status}</Badge>
            </header>
            <Score value={row.score} />
            <p>{row.detail}</p>
            {asArray(row.blocking_insights).length ? (
              <small>Limits: {row.blocking_insights.join(", ")}</small>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}

export function Analysis() {
  const [tab, setTab] = useState("command");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: payload } = await api.get("/analysis/intelligence", { skipGlobalLoader: true });
      setData(payload);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.post("/analysis/refresh", {}, { skipGlobalLoader: true });
      await load();
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => { load(); }, [load]);

  const metrics = data?.metrics || {};
  const active = useMemo(() => TABS.find((item) => item.key === tab) || TABS[0], [tab]);

  return (
    <Layout
      title="Analysis"
      subtitle="Commercial intelligence for demand, pricing, proposals, portfolio decisions and operational risk."
    >
      <div className="hx-analysis-page hx-intel-page" data-testid="analysis-page">
        <section className="hx-intel-hero">
          <div>
            <span>Commercial intelligence</span>
            <h2>{active.label}</h2>
            <p>{data?.caveat || "Analysis uses Hoardexa commercial records and does not claim audience measurement."}</p>
          </div>
          <button type="button" onClick={refresh} disabled={refreshing}>
            <RefreshCw size={15} className={refreshing ? "is-spinning" : ""} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
        </section>

        <section className="hx-analysis-metrics">
          <Metric label="Hoardings analyzed" value={fmtNumber(metrics.hoardings_analyzed, 0)} subtext="Commercial scorecards" icon={BarChart3} tone="blue" />
          <Metric label="Decision cards" value={fmtNumber(metrics.recommendations, 0)} subtext="Ranked actions" icon={Lightbulb} tone="amber" />
          <Metric label="Pricing moves" value={fmtNumber(metrics.pricing_moves, 0)} subtext="Non-fair price positions" icon={IndianRupee} tone="emerald" />
          <Metric label="Potential impact" value={inr(metrics.recoverable_impact_high)} subtext="High estimate across recommendations" icon={TrendingUp} tone="rose" />
        </section>

        <nav className="hx-analysis-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} type="button" className={tab === key ? "is-active" : ""} onClick={() => setTab(key)}>
              <Icon size={15} />
              {label}
            </button>
          ))}
        </nav>

        {loading ? <HoardexaLoader label="Loading commercial intelligence" /> : null}
        {!loading && !data ? <EmptyState title="Analysis unavailable" description="The intelligence payload could not be loaded." /> : null}
        {!loading && data && tab === "command" ? <CommandCenter data={data} /> : null}
        {!loading && data && tab === "scorecards" ? <Scorecards data={data} /> : null}
        {!loading && data && tab === "demand" ? <DemandCalendar data={data} /> : null}
        {!loading && data && tab === "pricing" ? <Pricing data={data} /> : null}
        {!loading && data && tab === "funnel" ? <ProposalFunnel data={data} /> : null}
        {!loading && data && tab === "portfolio" ? <Portfolio data={data} /> : null}
        {!loading && data && tab === "risks" ? <Risks data={data} /> : null}
        {!loading && data && tab === "confidence" ? <DataConfidence data={data} /> : null}

        {data?.last_updated ? (
          <p className="hx-intel-updated">
            <CheckCircle2 size={14} />
            Last refreshed {fmtDate(data.last_updated)}
          </p>
        ) : null}
      </div>
    </Layout>
  );
}
